import { useContext, useState } from 'react'

import { type SelectValue } from '@procurify/ui'
import { useDebouncedCallback } from 'use-debounce'

import { DataManagerSearch } from '@webapp/components/DataManager/components/DataManagerSearch'
import { usePagination } from '@webapp/core/hooks/usePagination'
import { type PaginationID } from '@webapp/core/providers/Pagination/pagination.types'
import { PaginationControlsContext } from '@webapp/core/providers/Pagination/PaginationControlsContext'
import {
  decodeSearchTerm,
  formatSearchWithCategory,
} from '../../utils/searchCategory'

export interface IPaginationSearchProps {
  id?: PaginationID
  autoFocus?: boolean
  placeholder?: string
  manualTrigger?: boolean
  categoryOptions?: SelectValue[]
}

export const PaginationSearch = ({
  id: idOverride,
  manualTrigger = false,
  autoFocus = false,
  placeholder,
  categoryOptions,
}: IPaginationSearchProps) => {
  const { id } = useContext(PaginationControlsContext)

  const paginationId = idOverride || id

  if (!paginationId) {
    throw new Error(
      'PaginationSearch must be used within a PaginationControls context or provide an id prop'
    )
  }

  const { params, updateParams } = usePagination({ id: paginationId })

  // The URL carries the composed `category:(term)`; the input shows the bare
  // term and the select shows the scope.
  const decodedSearch = decodeSearchTerm(params.search)

  const [searchValue, setSearchValue] = useState(decodedSearch.searchTerm)
  const [activeCategoryValue, setActiveCategoryValue] = useState<
    Nullable<string>
  >(decodedSearch.category ?? null)

  // Options arrive asynchronously (a search-operators query), so re-derive
  // rather than mirroring the option object in state — a deep-linked key
  // resolves to its label as soon as they land.
  const selectedCategory =
    categoryOptions?.find((option) => option.value === activeCategoryValue) ??
    categoryOptions?.[0]

  const commitSearch = (
    searchTerm: string,
    categoryValue: Nullable<string>
  ) => {
    updateParams({
      search: formatSearchWithCategory(searchTerm, categoryValue),
      page: 1,
    })
  }

  const updateManagerSearch = (searchTerm: string) => {
    commitSearch(searchTerm, activeCategoryValue)
  }

  const [showManualTriggerHelperText, setShowManualTriggerHelperText] =
    useState(false)

  const onChangeDebounced = useDebouncedCallback(updateManagerSearch, 250, {
    leading: false,
    trailing: true,
  })

  const onChangeTrottled = useDebouncedCallback(updateManagerSearch, 250, {
    leading: true,
    trailing: false,
  })

  const onInputChange = (searchTerm: string) => {
    setSearchValue(searchTerm)

    if (manualTrigger) {
      if (!searchTerm) {
        /**
         * Even when it is in manual trigger mode, we have to fire onChange when
         * the user is clearing the input either by emptying the input or pressing
         * the clear input button
         */
        setShowManualTriggerHelperText(false)
        onChangeDebounced(searchTerm)
      } else {
        setShowManualTriggerHelperText(true)
      }

      return
    }

    onChangeDebounced(searchTerm)
  }

  const onManualSearch = (searchTerm: string) => {
    setShowManualTriggerHelperText(false)

    onChangeTrottled(searchTerm)
  }

  // Re-scoping an existing term has to rewrite the param immediately —
  // otherwise the new scope only takes effect on the next keystroke. Under
  // manualTrigger the input can hold an unsubmitted draft, so re-scope what is
  // actually committed rather than firing a search the user never asked for.
  const handleCategoryChange = (newCategory: SelectValue) => {
    const nextCategory = newCategory?.value ?? null

    setActiveCategoryValue(nextCategory)

    commitSearch(
      manualTrigger ? decodedSearch.searchTerm : searchValue || '',
      nextCategory
    )
  }

  return (
    <DataManagerSearch
      value={searchValue || ''}
      category={selectedCategory}
      categoryOptions={categoryOptions}
      onCategoryChange={handleCategoryChange}
      onChange={onInputChange}
      onSearch={onManualSearch}
      showManualSearchText={showManualTriggerHelperText}
      autoFocus={autoFocus}
      placeholder={placeholder}
    />
  )
}
